import { useLingui } from "@lingui/react/macro";
import { FormError, FormStateSelect, IconButton, Input } from "@offstreetparking/design-system/forms";
import { CloseIcon } from "@offstreetparking/design-system/icons/CloseIcon";
import { cleanPlate } from "@offstreetparking/domain/entities/vehicle";
import type { CountryValue } from "@offstreetparking/geography";
import type { FieldErrors, Path, UseFormRegister } from "react-hook-form";

type VehicleInputProps<TFormValues extends { vehicles: { plate: string; state: string }[] }> = {
	index: number;
	register: UseFormRegister<TFormValues>;
	errors: FieldErrors<TFormValues>;
	country: CountryValue;
	showRemove: boolean;
	isDisabled?: boolean;
	onRemove: () => void;
};
export const VehicleInput = <TFormValues extends { vehicles: { plate: string; state: string }[] }>({
	index,
	register,
	errors,
	country,
	showRemove,
	isDisabled = false,
	onRemove,
}: VehicleInputProps<TFormValues>) => {
	const { t } = useLingui();
	const vehicleErrors = (
		errors.vehicles as
			| FieldErrors<{
					plate: string;
					state: string;
			  }>[]
			| undefined
	)?.[index];
	const plateError = (() => {
		const error = vehicleErrors?.plate;
		if (!error) {
			return undefined;
		}
		if (error.type === "too_small") {
			return t`License plate is required`;
		}
		if (error.message) {
			return error.message;
		}
		return t`Please enter a valid license plate (up to 10 characters)`;
	})();
	const stateError = vehicleErrors?.state ? t`State or province is required` : undefined;
	const hasError = !!plateError || !!stateError;
	const errorId = `vehicle-${index}-error`;
	return (
		<fieldset className="w-full" aria-describedby={hasError ? errorId : undefined}>
			<div className="flex w-full max-sm:items-end">
				<div className="min-w-[50%] flex-1">
					<Input
						{...register(`vehicles.${index}.plate` as Path<TFormValues>)}
						autoComplete="off"
						className="rounded-r-none"
						label={t`Plate`}
						name={`vehicles.${index}.plate`}
						onChange={(e) => {
							e.target.value = cleanPlate(e.target.value);
							register(`vehicles.${index}.plate` as Path<TFormValues>).onChange(e);
						}}
						aria-invalid={!!plateError}
						aria-describedby={hasError ? errorId : undefined}
						isRequired
						disabled={isDisabled}
					/>
					{plateError && <FormError error={plateError} className="mt-0.5 hidden sm:flex" />}
				</div>
				<div>
					<FormStateSelect
						label={t({
							message: "State / Province",
							comment:
								"Column header: the US state or Canadian province that issued the licence plate — not a status.",
						})}
						name={`vehicles.${index}.state`}
						className={showRemove ? "rounded-none" : "rounded-l-none"}
						country={country}
						hideError
						disabled={isDisabled}
					/>
					{stateError && <FormError error={stateError} className="mt-0.5 hidden sm:flex" />}
				</div>
				{showRemove && (
					<IconButton
						className="mt-5 rounded-l-none"
						aria-label={t`Remove`}
						icon={CloseIcon}
						intent="secondary"
						size="md"
						type="button"
						isDisabled={isDisabled}
						onClick={onRemove}
					/>
				)}
			</div>
			{hasError && (
				<div className="mt-0.5 sm:hidden" id={errorId}>
					{plateError && <FormError error={plateError} />}
					{stateError && <FormError error={stateError} />}
				</div>
			)}
		</fieldset>
	);
};
